import React, { useState } from "react"

import { trpc } from "../../lib/trpc"

interface ScheduledPostsProps {
  selectedBrand: string | null
}

const ScheduledPosts: React.FC<ScheduledPostsProps> = ({ selectedBrand }) => {
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [viewMode, setViewMode] = useState<"calendar" | "list">("list")
  const [scheduleModalContent, setScheduleModalContent] = useState<any>(null)

  // Calculate date range for current week/month view
  const startDate = new Date(selectedDate)
  startDate.setDate(startDate.getDate() - 7) // Show past week too
  const endDate = new Date(selectedDate)
  endDate.setDate(endDate.getDate() + 30) // Show next month

  const {
    data: scheduledPosts,
    isLoading,
    refetch,
  } = trpc.contentManagement.getScheduledPosts.useQuery({
    brandId: selectedBrand || undefined,
    startDate,
    endDate,
  })

  const { data: readyContent } = trpc.contentManagement.listContent.useQuery({
    brandId: selectedBrand || undefined,
    status: "ready",
    limit: 20,
  })

  // const schedulePostMutation = trpc.contentManagement.schedulePost.useMutation(); // TODO: Add to backend
  const updateScheduleMutation = trpc.contentManagement.updateScheduledPost.useMutation()
  const cancelScheduleMutation = trpc.contentManagement.cancelScheduledPost.useMutation()
  // const publishNowMutation = trpc.contentManagement.publishNow.useMutation(); // TODO: Add this mutation to backend

  const handleSchedulePost = (contentId: string, scheduledTime: Date, platforms: string[]) => {
    try {
      // TODO: Implement schedulePost mutation in backend
      // await schedulePostMutation.mutateAsync({
      //   contentId,
      //   scheduledTime,
      //   platforms,
      // });
      console.log("Schedule post:", { contentId, scheduledTime, platforms })
      setScheduleModalContent(null)
      void refetch()
    } catch (error) {
      console.error("Failed to schedule post:", error)
    }
  }

  const handleUpdateSchedule = async (scheduledPostId: string, newTime: Date) => {
    try {
      await updateScheduleMutation.mutateAsync({
        contentItemId: scheduledPostId,
        scheduledTime: newTime,
      })
      void refetch()
    } catch (error) {
      console.error("Failed to update schedule:", error)
    }
  }

  const handleCancelSchedule = async (scheduledPostId: string) => {
    try {
      await cancelScheduleMutation.mutateAsync({ contentItemId: scheduledPostId })
      void refetch()
    } catch (error) {
      console.error("Failed to cancel schedule:", error)
    }
  }

  const handlePublishNow = (contentId: string, platforms: string[]) => {
    try {
      // TODO: Implement publishNow mutation in backend
      // await publishNowMutation.mutateAsync({ contentId, platforms });
      console.log("Publish now:", { contentId, platforms })
      void refetch()
    } catch (error) {
      console.error("Failed to publish now:", error)
    }
  }

  // Group posts by date for calendar view
  const postsByDate = React.useMemo(() => {
    const grouped: Record<string, any[]> = {}
    scheduledPosts?.forEach((post: any) => {
      const date = new Date(post.content.scheduledAt).toDateString()
      if (!grouped[date]) grouped[date] = []
      grouped[date].push(post)
    })
    return grouped
  }, [scheduledPosts])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold ">Scheduled Posts</h2>
          <p className="">Manage your scheduled content</p>
        </div>
        <div className="flex space-x-3">
          <div className="flex bg-foregroundx rounded-lg p-1">
            <button
              onClick={() => setViewMode("list")}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                viewMode === "list" ? "bg-background  shadow-sm" : ""
              }`}
            >
              List
            </button>
            <button
              onClick={() => setViewMode("calendar")}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                viewMode === "calendar" ? "bg-background  shadow-sm" : ""
              }`}
            >
              Calendar
            </button>
          </div>
          <button
            onClick={() => setScheduleModalContent({})}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Schedule Post
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-background p-4 rounded-lg shadow">
          <div className="text-2xl font-bold ">
            {scheduledPosts?.filter((p: any) => p.content.status === "scheduled").length || 0}
          </div>
          <div className="text-sm text-gray-500">Scheduled</div>
        </div>
        <div className="bg-background p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-green-600">
            {scheduledPosts?.filter((p: any) => p.content.status === "published").length || 0}
          </div>
          <div className="text-sm text-gray-500">Published</div>
        </div>
        <div className="bg-background p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-destructive">
            {scheduledPosts?.filter((p: any) => p.content.status === "failed").length || 0}
          </div>
          <div className="text-sm text-gray-500">Failed</div>
        </div>
        <div className="bg-background p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-yellow-600">{readyContent?.length || 0}</div>
          <div className="text-sm text-gray-500">Ready to Schedule</div>
        </div>
      </div>

      {viewMode === "list" ? (
        /* List View */
        <div className="space-y-4">
          {scheduledPosts?.map((post: any) => (
            <div key={post.content.id} className="bg-background p-6 rounded-lg shadow">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-medium ">{post.content.title || "Untitled Post"}</h3>
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        post.content.status === "scheduled"
                          ? "bg-blue-100 text-blue-800"
                          : post.content.status === "published"
                            ? "bg-green-100 text-green-800"
                            : post.content.status === "failed"
                              ? "bg-red-100 text-destructive"
                              : "bg-foregroundx text-gray-800"
                      }`}
                    >
                      {post.content.status}
                    </span>
                  </div>

                  <p className=" mb-3 line-clamp-2">{post.content.body}</p>

                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>🏢 {post.brand?.name || "No brand"}</span>
                    <span>📅 {new Date(post.content.scheduledAt).toLocaleString()}</span>
                    {post.content.platformVersions && (
                      <span>📱 {Object.keys(post.content.platformVersions).join(", ")}</span>
                    )}
                  </div>
                </div>

                <div className="flex space-x-2">
                  {post.content.status === "scheduled" && (
                    <>
                      <button
                        onClick={() =>
                          handlePublishNow(post.content.id, Object.keys(post.content.platformVersions || {}))
                        }
                        disabled={false}
                        className="px-3 py-1 bg-green-100 text-green-700 rounded text-sm hover:bg-green-200 transition-colors disabled:opacity-50"
                      >
                        Publish Now
                      </button>
                      <button
                        onClick={() => handleCancelSchedule(post.content.id)}
                        disabled={cancelScheduleMutation.isLoading}
                        className="px-3 py-1 bg-red-100 text-destructive rounded text-sm hover:bg-red-200 transition-colors disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </>
                  )}

                  {post.content.status === "failed" && (
                    <button className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200 transition-colors">
                      Retry
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {(!scheduledPosts || scheduledPosts.length === 0) && (
            <div className="text-center py-12">
              <div className="text-gray-400 text-4xl mb-4">📅</div>
              <h3 className="text-lg font-medium  mb-2">No scheduled posts</h3>
              <p className=" mb-4">Schedule your first post to get started</p>
              <button
                onClick={() => setScheduleModalContent({})}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Schedule Post
              </button>
            </div>
          )}
        </div>
      ) : (
        /* Calendar View */
        <div className="bg-background rounded-lg shadow">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium ">
                {selectedDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
              </h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    const newDate = new Date(selectedDate)
                    newDate.setMonth(newDate.getMonth() - 1)
                    setSelectedDate(newDate)
                  }}
                  className="p-2  hover:"
                >
                  ←
                </button>
                <button
                  onClick={() => setSelectedDate(new Date())}
                  className="px-3 py-1 bg-foregroundx text-primary rounded "
                >
                  Today
                </button>
                <button
                  onClick={() => {
                    const newDate = new Date(selectedDate)
                    newDate.setMonth(newDate.getMonth() + 1)
                    setSelectedDate(newDate)
                  }}
                  className="p-2  hover:"
                >
                  →
                </button>
              </div>
            </div>

            {/* Simple calendar grid */}
            <div className="grid grid-cols-7 gap-4">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}

              {/* Generate calendar days */}
              {Array.from({ length: 35 }, (_, i) => {
                const date = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), i - 6)
                const dateString = date.toDateString()
                const postsForDay = postsByDate[dateString] || []
                const isToday = date.toDateString() === new Date().toDateString()
                const isCurrentMonth = date.getMonth() === selectedDate.getMonth()

                return (
                  <div
                    key={i}
                    className={`min-h-[80px] p-2 border rounded ${isCurrentMonth ? "bg-background" : "bg-foreground"} ${
                      isToday ? "ring-2 ring-blue-500" : ""
                    }`}
                  >
                    <div className={`text-sm ${isCurrentMonth ? "" : "text-gray-400"}`}>{date.getDate()}</div>
                    <div className="space-y-1 mt-1">
                      {postsForDay.slice(0, 2).map((post: any, idx: number) => (
                        <div
                          key={idx}
                          className={`text-xs p-1 rounded truncate ${
                            post.content.status === "scheduled"
                              ? "bg-blue-100 text-blue-800"
                              : post.content.status === "published"
                                ? "bg-green-100 text-green-800"
                                : "bg-foregroundx text-gray-800"
                          }`}
                        >
                          {post.content.title || "Untitled"}
                        </div>
                      ))}
                      {postsForDay.length > 2 && (
                        <div className="text-xs text-gray-500">+{postsForDay.length - 2} more</div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Schedule Post Modal */}
      {scheduleModalContent && (
        <SchedulePostModal
          onClose={() => setScheduleModalContent(null)}
          onSubmit={handleSchedulePost}
          isLoading={false}
          readyContent={readyContent || []}
          selectedBrand={selectedBrand}
        />
      )}
    </div>
  )
}

const SchedulePostModal: React.FC<{
  onClose: () => void
  onSubmit: (contentId: string, scheduledTime: Date, platforms: string[]) => void
  isLoading: boolean
  readyContent: any[]
  selectedBrand: string | null
}> = ({ onClose, onSubmit, isLoading, readyContent, selectedBrand }) => {
  const [selectedContent, setSelectedContent] = useState("")
  const [scheduledDate, setScheduledDate] = useState(
    new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16), // 1 hour from now
  )
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([])

  const content = readyContent.find((c) => c.content.id === selectedContent)
  const availablePlatforms = content?.content.platformVersions ? Object.keys(content.content.platformVersions) : []

  const platforms = [
    { id: "linkedin", name: "LinkedIn", icon: "💼" },
    { id: "twitter", name: "Twitter/X", icon: "🐦" },
  ]

  const togglePlatform = (platform: string) => {
    setSelectedPlatforms((prev) => (prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (selectedContent && selectedPlatforms.length > 0) {
      onSubmit(selectedContent, new Date(scheduledDate), selectedPlatforms)
    }
  }

  // Auto-select available platforms when content is selected
  React.useEffect(() => {
    if (availablePlatforms.length > 0) {
      setSelectedPlatforms(availablePlatforms)
    }
  }, [availablePlatforms])

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-medium  mb-4">Schedule Post</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Select Content</label>
            <select
              required
              value={selectedContent}
              onChange={(e) => setSelectedContent(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Choose content to schedule</option>
              {readyContent
                .filter((c) => !selectedBrand || c.content.brandId === selectedBrand)
                .map((item: any) => (
                  <option key={item.content.id} value={item.content.id}>
                    {item.content.title || item.content.body.substring(0, 50) + "..."}
                    {item.brand ? ` - ${item.brand.name}` : ""}
                  </option>
                ))}
            </select>
          </div>

          {content && (
            <div className="bg-foregroundx p-4 rounded-lg">
              <h4 className="font-medium  mb-2">Content Preview</h4>
              <p className="text-primary text-sm line-clamp-3">{content.content.body}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Scheduled Time</label>
            <input
              type="datetime-local"
              required
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              min={new Date().toISOString().slice(0, 16)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {availablePlatforms.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-primary mb-2">Select Platforms</label>
              <div className="space-y-2">
                {platforms
                  .filter((platform) => availablePlatforms.includes(platform.id))
                  .map((platform) => (
                    <label key={platform.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedPlatforms.includes(platform.id)}
                        onChange={() => togglePlatform(platform.id)}
                        className="h-4 w-4  focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-primary">
                        {platform.icon} {platform.name}
                      </span>
                    </label>
                  ))}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-primary bg-foregroundx rounded-md  transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || selectedPlatforms.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isLoading ? "Scheduling..." : "Schedule Post"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ScheduledPosts
